/**
 * Central env adapter: Vite-first, reads from import.meta.env.
 * Use this instead of process.env so runtime stays testable and build stays Vite-native.
 */

type EnvKey =
  | 'VITE_API'
  | 'VITE_URL'
  | 'VITE_E2E_TEST'
  | 'VITE_TURNSTILE_SITE_KEY'
  | 'VITE_DISCORD_INVITE_URL'
  | 'VITE_WHATSAPP_INVITE_URL'
  | 'VITE_SHOW_BETA_HINT'
  | 'VITE_LOG_LEVEL'
  | 'VITE_ENABLE_GAMEPLAY_HISTORY_REPLAY'
  | 'VITE_ENABLE_PAYMENT_DEBUG';

function getEnvValue(key: EnvKey): string | undefined {
  const meta = typeof import.meta !== 'undefined' ? import.meta.env : {};
  const val = (meta as Record<string, string | undefined>)[key];
  return val;
}

export const env = {
  /** Vite build mode: 'development' | 'production' | 'test' | custom */
  get mode() {
    return typeof import.meta !== 'undefined' ? import.meta.env.MODE : 'development';
  },
  get dev() {
    return typeof import.meta !== 'undefined' ? import.meta.env.DEV : true;
  },
  get prod() {
    return typeof import.meta !== 'undefined' ? import.meta.env.PROD : false;
  },
  get test() {
    return this.mode === 'test';
  },
  get api() {
    return getEnvValue('VITE_API');
  },
  get url() {
    return getEnvValue('VITE_URL');
  },
  get e2eTest() {
    return getEnvValue('VITE_E2E_TEST');
  },
  get turnstileSiteKey() {
    return getEnvValue('VITE_TURNSTILE_SITE_KEY');
  },
  get discordInviteUrl() {
    return getEnvValue('VITE_DISCORD_INVITE_URL');
  },
  get whatsappInviteUrl() {
    return getEnvValue('VITE_WHATSAPP_INVITE_URL');
  },
  get showBetaHint() {
    return getEnvValue('VITE_SHOW_BETA_HINT');
  },
  get logLevel() {
    return getEnvValue('VITE_LOG_LEVEL');
  },
  /**
   * Feature gate: gameplay history + replay (end screen replay button, /games history, /games/:id/replay, in-game history panel).
   * Expected values: 'true' | 'false' | undefined (defaults dev-on, prod-off).
   */
  get enableGameplayHistoryReplay() {
    return getEnvValue('VITE_ENABLE_GAMEPLAY_HISTORY_REPLAY');
  },
  get enablePaymentDebug() {
    return getEnvValue('VITE_ENABLE_PAYMENT_DEBUG');
  },
};
